
import { Component, Ref, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";

import { CameraReferenceShot, DeviceDetails, DevicesState, ServerDetails, ServerType } from "@/store/devices/types";
import { ResourceType, UpdateSessionParams } from "@/store/sessions/types";
import { PrefTypesEnum, PrefTypeRequest } from '@/store/settings/types';

const SettingsStore = namespace("settings");

const Devices = namespace("devices");
const SessionStore = namespace("sessions");
const SMCameras = namespace("siteMonitorCameras");

const PrefsOfInterest = {
	UseMechanicalPtzByDefault: {
		groupID: null,
		prefTypeID: PrefTypesEnum.UseMechanicalPtzByDefault
	} as PrefTypeRequest
};

@Component({})
export default class CameraPreview extends Vue {
	@Devices.Action private getServer: (serverId: number) => Promise<void>;
	@Devices.Mutation private setCameraPreviewModalVisibility: (visible: boolean) => void;
	@Devices.Mutation private setCameraDefaultReferenceShot: (params: CameraReferenceShot) => void;
	@Devices.State((state: DevicesState) => state.device) private device: DeviceDetails;
	@Devices.State((state: DevicesState) => state.serversList) private serversList: ServerDetails[];
	@Devices.State((state: DevicesState) => state.serverTypes) private serverTypes: ServerType[];

	@SessionStore.Action private updateSession: (params: UpdateSessionParams) => Promise<void>;
	@SMCameras.Action private setCameraScreenshot: (params: CameraReferenceShot) => Promise<void>;

	@SettingsStore.Action getPrefsFromList: (prefEnumList: PrefTypeRequest[]) => void;
	@SettingsStore.Getter getPrefBoolValue: (prefType: PrefTypeRequest) => boolean;

	@Ref() private cameraControl: any;

	private get shouldDefaultToMechanicalPtz(): boolean {
		return this.getPrefBoolValue(PrefsOfInterest.UseMechanicalPtzByDefault);
	}

	private handleModalHidden(): void {
		this.setCameraPreviewModalVisibility(false);
	}

	private async created(): Promise<void> {
		if (this.deviceServerNotInList) {
			await this.getServer(this.device.serverID);
		}
	}

	private async mounted(): Promise<void> {
		await this.getPrefsFromList([PrefsOfInterest.UseMechanicalPtzByDefault]);
		await this.updateSession({ resourceId: ResourceType.Proxy });
		await this.cameraControl.startCamera(this.device.deviceID);
	}

	private get deviceServerNotInList(): boolean {
		return this.deviceServer === undefined;
	}

	private get title(): string {
		return `Preview: ${this.device.title}`;
	}

	private get deviceServer(): ServerDetails {
		return this.serversList.find(s => s.serverID === this.device.serverID);
	}

	private get serverType(): ServerType {
		return this.serverTypes.find(st => st.serverTypeID == this.deviceServer?.serverTypeID);
	}

	private get usesInput2(): boolean {
		return this.serverType?.usesExtraCameraIds === true;
	}

	private get usesExtraValue(): boolean {
		return this.serverType?.usesDeviceExtraValue === true;
	}

	private async handleTakeReferenceShotClick(): Promise<void> {
		//tech debt - remove usage of refs if V2Cameras is changed to accomodate props
		var canvas = document.createElement("canvas");

		canvas.width = this.cameraControl.$refs.video.$refs.videoControl.videoWidth;
		canvas.height = this.cameraControl.$refs.video.$refs.videoControl.videoHeight;

		canvas
			.getContext("2d")
			.drawImage(this.cameraControl.$refs.video.$refs.videoControl, 0, 0, canvas.width, canvas.height);

		const image = canvas.toDataURL().split(";base64,")[1];
		const cameraReferenceShot = { DeviceID: this.device.deviceID, Image: image };

		this.setCameraDefaultReferenceShot(cameraReferenceShot);
		await this.setCameraScreenshot(cameraReferenceShot);
	}
}
